import { atom, type Getter, type Setter } from 'jotai';
import { type WorkerMessage, type WorkerResponse } from './worker';
import type { Pipeline } from 'src/api-new/bifrost';

/**
 * Generates a deterministic hash from a pipeline object.
 * Uses a simple djb2 hash algorithm on the JSON string representation.
 */
export function hashPipeline(data: unknown): string {
  const str = JSON.stringify(data);
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit integer and then to hex string
  return (hash >>> 0).toString(16);
}

export type FileStatus =
  | { status: 'idle'; file: string }
  | { status: 'running'; file: string }
  | { status: 'error'; message: string; file: string }
  | { status: 'complete'; file: string };

export type PipelineState = {
  /** Per-file progress/errors */
  files: Array<FileStatus>;
  pipelines: Array<{ name: string; id: string }>;
};

function upsertByFile(prev: Array<FileStatus>, next: FileStatus): Array<FileStatus> {
  const idx = prev.findIndex((s) => s.file === next.file);
  if (idx === -1) return [...prev, next];
  const updated = prev.slice();
  updated[idx] = next;
  return updated;
}

// Internal atoms
const filesAtom = atom<Array<File>>([]);
const stateAtom = atom<PipelineState>({ files: [], pipelines: [] });
const workerAtom = atom<Worker | null>(null);
const pipelinesPerFileAtom = atom<Map<string, Array<{ name: string; id: string }>>>(new Map());
const pipelineDataPerFileAtom = atom<Map<string, Array<Pipeline>>>(new Map());

function aggregatePipelines(
  map: Map<string, Array<{ name: string; id: string }>>,
): Array<{ name: string; id: string }> {
  const seen = new Set<string>();
  const result: Array<{ name: string; id: string }> = [];
  for (const pipelines of map.values()) {
    for (const p of pipelines) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        result.push(p);
      }
    }
  }
  return result;
}

export const pipelineDataAtom = atom((get) => {
  const allPipelines = [...get(pipelineDataPerFileAtom).values()].flat();
  const seen = new Set<string>();
  const result: Pipeline[] = [];
  for (const p of allPipelines) {
    // Deduplicate by hash (exact same content = same pipeline)
    const hash = hashPipeline(p);
    if (!seen.has(hash)) {
      seen.add(hash);
      result.push(p);
    }
  }
  return result;
});

function resetAll(get: Getter, set: Setter) {
  const currentWorker = get(workerAtom) as Worker | null;
  if (currentWorker) currentWorker.terminate();
  set(workerAtom, null);
  set(filesAtom, []);
  set(pipelinesPerFileAtom, new Map());
  set(pipelineDataPerFileAtom, new Map());
  set(stateAtom, { files: [], pipelines: [] });
}

type PipelineImportsUpdate = Array<File> | ((prev: Array<File>) => Array<File>) | { type: 'reset' };

function isResetAction(value: PipelineImportsUpdate): value is { type: 'reset' } {
  return typeof value === 'object' && !Array.isArray(value) && 'type' in value && value.type === 'reset';
}

export const resetPipelineImportsAtom = atom(null, (get, set) => {
  resetAll(get, set);
});

export const pipelineImportsAtom = atom(
  (get) => get(stateAtom),
  (get, set, nextFilesOrUpdater: PipelineImportsUpdate) => {
    if (isResetAction(nextFilesOrUpdater)) {
      resetAll(get, set);
      return;
    }

    const prevFiles = get(filesAtom);
    const nextFiles = typeof nextFilesOrUpdater === 'function' ? nextFilesOrUpdater(prevFiles) : nextFilesOrUpdater;
    const prevState = get(stateAtom);

    // Calculate which files were added and removed
    const prevFileNames = new Set(prevFiles.map((f) => f.name));
    const nextFileNames = new Set(nextFiles.map((f) => f.name));

    const addedFiles = nextFiles.filter((f) => !prevFileNames.has(f.name));
    const removedFileNames = [...prevFileNames].filter((name) => !nextFileNames.has(name));

    // Always store the selected files
    set(filesAtom, nextFiles);

    // Empty selection => clear everything
    if (nextFiles.length === 0) {
      resetAll(get, set);
      return;
    }

    // Handle removals - remove their pipelines and file statuses (no reprocessing needed)
    if (removedFileNames.length > 0) {
      const pipelinesPerFile = new Map(get(pipelinesPerFileAtom));
      const pipelineDataPerFile = new Map(get(pipelineDataPerFileAtom));

      for (const fileName of removedFileNames) {
        pipelinesPerFile.delete(fileName);
        pipelineDataPerFile.delete(fileName);
      }

      set(pipelinesPerFileAtom, pipelinesPerFile);
      set(pipelineDataPerFileAtom, pipelineDataPerFile);

      // Filter out file statuses for removed files
      const remainingFiles = prevState.files.filter((f) => nextFileNames.has(f.file));

      set(stateAtom, {
        ...prevState,
        files: remainingFiles,
        pipelines: aggregatePipelines(pipelinesPerFile),
      });
    }

    // If no new files to process, we're done (removal-only case)
    if (addedFiles.length === 0) {
      return;
    }

    // Kill any previous worker if still running (for new file processing)
    const currentWorker = get(workerAtom);
    if (currentWorker) currentWorker.terminate();
    set(workerAtom, null);

    // Add idle status for new files only
    const newFileStatuses: FileStatus[] = addedFiles.map((f) => ({ status: 'idle', file: f.name }));
    const currentState = get(stateAtom);
    set(stateAtom, {
      ...currentState,
      files: [...currentState.files, ...newFileStatuses],
    });

    const worker = new Worker(new URL('./worker.ts', import.meta.url));
    set(workerAtom, worker);

    const updateState = (fn: (prev: PipelineState) => PipelineState) => {
      const prev = get(stateAtom);
      set(stateAtom, fn(prev));
    };

    worker.addEventListener('message', (e: MessageEvent<WorkerResponse>) => {
      const msg = e.data;

      switch (msg.type) {
        case 'FILE_START': {
          updateState((prev) => ({
            ...prev,
            files: upsertByFile(prev.files, { status: 'running', file: msg.fileName }),
          }));
          break;
        }

        case 'FILE_COMPLETE': {
          updateState((prev) => {
            const current = prev.files.find((s) => s.file === msg.fileName);
            // Don't overwrite an error with "complete"
            if (current?.status === 'error') return prev;

            return {
              ...prev,
              files: upsertByFile(prev.files, { status: 'complete', file: msg.fileName }),
            };
          });
          break;
        }

        case 'PIPELINES': {
          const pipelinesPerFile = new Map(get(pipelinesPerFileAtom));
          const pipelineDataPerFile = new Map(get(pipelineDataPerFileAtom));

          // Store pipelines per file for incremental updates
          for (const p of msg.messages) {
            const id = hashPipeline(p.data);

            // Store metadata for UI - deduplicate by hash within file
            const existing = pipelinesPerFile.get(p.fileName) ?? [];
            if (!existing.some((e) => e.id === id)) {
              existing.push({ name: p.name, id });
              pipelinesPerFile.set(p.fileName, existing);
            }

            // Store full data for import - deduplicate by hash within file
            const existingData = pipelineDataPerFile.get(p.fileName) ?? [];
            if (!existingData.some((e) => hashPipeline(e) === id)) {
              existingData.push(p.data as Pipeline);
              pipelineDataPerFile.set(p.fileName, existingData);
            }
          }

          set(pipelinesPerFileAtom, pipelinesPerFile);
          set(pipelineDataPerFileAtom, pipelineDataPerFile);

          updateState((prev) => ({
            ...prev,
            pipelines: aggregatePipelines(pipelinesPerFile),
          }));
          break;
        }

        case 'ERROR': {
          updateState((prev) => ({
            ...prev,
            files: upsertByFile(prev.files, {
              status: 'error',
              message: msg.message,
              file: msg.fileName,
            }),
          }));
          break;
        }

        case 'DONE': {
          // Processing complete - all data is already stored via PIPELINES messages
          break;
        }

        default: {
          // noop
          break;
        }
      }
    });

    // Only send new files to process (not all files)
    worker.postMessage({ type: 'VALIDATE_FILES', files: addedFiles } satisfies WorkerMessage);
  },
);
